/**
 * AWS Cognito SRP Authentication
 * Simple SRP authentication for password security
 */

import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
} from "amazon-cognito-identity-js";

const newPoolData = {
  UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID || "",
  ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID || "",
};

const legacyPoolData = {
  UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID_LEGACY || "",
  ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID_LEGACY || "",
};

const newUserPool = new CognitoUserPool(newPoolData);
const legacyUserPool = new CognitoUserPool(legacyPoolData);

/**
 * Sign in user with SRP authentication to the new Cognito pool
 * @param {string} username - User's email/username
 * @param {string} password - User's password
 * @returns {Promise<Object>} Promise resolving to user data with JWT token for authentication
 */
export const signInNew = (username, password) => {
  return new Promise((resolve, reject) => {
    const authenticationDetails = new AuthenticationDetails({
      Username: username,
      Password: password,
    });

    const cognitoUser = new CognitoUser({
      Username: username,
      Pool: newUserPool,
    });

    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: (result) => {
        // SRP authentication successful - password was verified securely
        // Return JWT token for socket authentication
        resolve({
          username: username,
          idToken: result.getIdToken().getJwtToken(),
          accessToken: result.getAccessToken().getJwtToken(),
          refreshToken: result.getRefreshToken().getToken(),
        });
      },

      onFailure: (err) => {
        reject(err);
      },

      mfaRequired: (challengeName, challengeParameters) => {
        reject({
          code: "MFARequired",
          challengeName,
          challengeParameters,
          message: "MFA verification required",
        });
      },

      newPasswordRequired: (userAttributes, requiredAttributes) => {
        reject({
          code: "NewPasswordRequired",
          userAttributes,
          requiredAttributes,
          message: "New password required",
        });
      },
    });
  });
};

/**
 * Sign in user with SRP authentication to the legacy Cognito pool
 * @param {string} username - User's email/username
 * @param {string} password - User's password
 * @returns {Promise<Object>} Promise resolving to user data with JWT token for authentication
 */
export const signInLegacy = (username, password) => {
  return new Promise((resolve, reject) => {
    const authenticationDetails = new AuthenticationDetails({
      Username: username,
      Password: password,
    });

    const cognitoUser = new CognitoUser({
      Username: username,
      Pool: legacyUserPool,
    });

    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: (result) => {
        // SRP authentication successful - password was verified securely
        // Return JWT token for socket authentication
        resolve({
          username: username,
          idToken: result.getIdToken().getJwtToken(),
          accessToken: result.getAccessToken().getJwtToken(),
          refreshToken: result.getRefreshToken().getToken(),
        });
      },

      onFailure: (err) => {
        reject(err);
      },

      mfaRequired: (challengeName, challengeParameters) => {
        reject({
          code: "MFARequired",
          challengeName,
          challengeParameters,
          message: "MFA verification required",
        });
      },

      newPasswordRequired: (userAttributes, requiredAttributes) => {
        reject({
          code: "NewPasswordRequired",
          userAttributes,
          requiredAttributes,
          message: "New password required",
        });
      },
    });
  });
};

/**
 * Legacy signIn function - kept for backward compatibility
 * @deprecated Use signInNew instead
 */
export const signIn = signInNew;
